import {
  Dialog,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { AlertType, appendAlertItem, AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ProTableCell from 'src/app/common/components/ProTable/ProTableCell';
import StickyTableCell from 'src/app/common/components/ProTable/StickyTableCell';
import { PruForm } from 'src/app/common/components/PruForm';
import PruTableEmptyRow from 'src/app/common/components/Table/PruTableEmptyRow';
import PruTableLoading from 'src/app/common/components/Table/PruTableLoading';
import PruTablePaginationActions from 'src/app/common/components/Table/PruTablePaginationActions';
import { regionLocale } from 'src/app/i18n';
import { addTemplate, editTemplate, getTemplates } from 'src/app/modules/Event/network/greetingCardCrud';
import { RootState } from 'src/redux/store';
import { getSpecialDays } from '../../../../network/specialDayCrud';
import { getSpecialLabel } from '../../../../network/utils';
import { PublishStatus } from '../../../../types/greeting-card-type';
import { styles } from '../styles';
import DeleteDialog from './components/deleteDialog';
import PopUp from './components/popUp';
import filterConfig from './filterConfig';
import tableConfig from './tableConfig';
import tableHeader from './tableHeader';
import { SpecialDayItem } from 'src/app/modules/Event/types/special-day-type';

type DeleteDialogState = {
  open: boolean;
  id: string;
};

const initialDeleteDialogState: DeleteDialogState = {
  open: false,
  id: '',
};

const TemplateList: FC<any> = (props) => {
  const intl = useIntl();
  const { classes } = styles();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const filterInitData = {
    status: PublishStatus.ALL,
    // timeRange: [getDayStart(new Date()), getDayEnd(new Date())]
  };

  const [filterData, setFilterData] = useState<{ [key: string]: any }>(filterInitData);
  const [filterSavedData, setFilterSavedData] = useState<{ [key: string]: any }>(filterInitData);
  const [tableData, setTableData] = useState<{ [key: string]: any }[]>([]);
  const [pageData, setPageData] = useState<{ [key: string]: any }>({
    pageNum: 1,
    pageSize: 10,
  });
  const [popUpVisible, setPopUpVisible] = useState<boolean>(false);
  const initialTemplateValue = {
    status: PublishStatus.PUBLISHED,
  };

  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(initialTemplateValue);
  const [listSpecialDay, setListSpecialDay] = useState<SpecialDayItem[]>([]);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [templateId, setTemplateId] = useState<string>('');
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>(initialDeleteDialogState);

  const fetchAPI = async () => {
    await fetchData();
    await initialListData();
  };

  useEffect(() => {
    fetchAPI();
  }, []);

  useEffect(() => {
    window.addEventListener('keypress', keyPressSearch);
    return () => {
      window.removeEventListener('keypress', keyPressSearch);
    };
    // eslint-disable-next-line
  }, [filterData]);

  const initialListData = async () => {
    const specificDay = await getSpecialDays({
      pageNum: 1,
      pageSize: 1000,
      status: PublishStatus.PUBLISHED,
    });
    if (specificDay.values) {
      setListSpecialDay(specificDay.values);
    }
  };

  const options = useMemo(() => {
    return listSpecialDay?.map((item: any) => ({
      label: item?.name ? item?.name : getSpecialLabel(item, intl.locale, false),
      value: item?.specialDayId,
    }));
  }, [listSpecialDay]);

  const fetchData = (newFilterState?: any) => {
    setPageLoading(true);
    const newFilter = newFilterState || {};
    const requestParams = {
      ...filterData,
      ...pageData,
      ...newFilter,
    };

    if (requestParams.status == PublishStatus.ALL) {
      delete requestParams.status;
    }
    if (requestParams.timeRange) {
      requestParams.startDate = requestParams.timeRange?.[0] || '';
      requestParams.endDate = requestParams.timeRange?.[1] || '';
    }
    delete requestParams.timeRange;

    getTemplates(requestParams)
      .then((res: any) => {
        setTableData(res.data?.values || []);
        setPageData({
          pageNum: res.data?.page || 1,
          pageSize: res.data?.size || 0,
          total: res.data?.total || 0,
        });
      })
      .catch((err: any) => {
        console.log(err.message);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const onSearch = () => {
    // setFilterData(filterData)
    const newPageData = {
      ...pageData,
      pageNum: 1,
    };
    setPageData(newPageData);
    setFilterSavedData(filterData);
    fetchData({ ...filterData, ...newPageData });
  };

  const keyPressSearch = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onChangePage = (pageNum: number) => {
    const newPageData = {
      ...pageData,
      pageNum,
    };
    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const onChangePageRows = (rows: any) => {
    const newPageData = {
      ...pageData,
      pageNum: 1,
      pageSize: rows,
    };
    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const handleSort = (sorter: any) => {
    let sortItem;
    let sortOrder;

    Object.entries(sorter).forEach(([item, order]) => {
      sortItem = item;
      sortOrder = order;
    });

    const data = {
      ...filterSavedData,
      sortItem,
      sortOrder: sortOrder ? (sortOrder as string).toUpperCase() : sortOrder,
    };

    const newPageData = {
      ...pageData,
      pageNum: 1,
    };

    setFilterSavedData(data);
    setPageData(newPageData);
    fetchData({ ...data, ...newPageData });
  };

  const openPopUp = () => {
    setPopUpVisible(true);
  };

  const onSave = async (values: any) => {
    setFormLoading(true);

    const data = {
      ...values,
      templateId,
      specificDay: listSpecialDay.find((item: any) => item.specialDayId === values.specificDay) || null,
      modifier: user && user.username,
    };

    try {
      if (templateId) {
        await editTemplate(data);
      } else {
        await addTemplate(data);
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('global.submit.success'),
            content: Translation('global.submit.success'),
          },
        ]),
      );
      await refreshData();
      setPopUpVisible(false);
    } catch (error: any) {
      const errorCode = error?.code;
      if (errorCode === 'E010') {
        return dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: `Templates cannot be ${templateId ? 'updated' : 'created'} for unpublished special days`,
            },
          ]),
        );
      }
      return dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: 'Failed, please try again.',
          },
        ]),
      );
    } finally {
      setFormLoading(false);
    }
  };

  const refreshData = async () => {
    const newPageData = {
      ...pageData,
      pageNum: 1,
    };
    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const getValidSpecificDay = (specificDay?: string) =>
    specificDay && options.some((item: any) => item.value === specificDay) ? specificDay : null;

  const onEdit = (row: any) => {
    setTemplateId(row.templateId);
    setPopUpVisible(true);
    setInitialValues({
      ...row,
      ...{ status: row.status, specificDay: getValidSpecificDay(row.specificDay?.specialDayId) },
    });
  };

  const onCopy = (row: any) => {
    setTemplateId('');
    setInitialValues({
      ...row,
      status: PublishStatus.UNPUBLISHED,
      specificDay: getValidSpecificDay(row.specificDay?.specialDayId),
    });
    setPopUpVisible(true);
  };

  const onChangeStatus = (row: any) => {
    editTemplate({
      ...row,
      status: row.status == PublishStatus.PUBLISHED ? PublishStatus.UNPUBLISHED : PublishStatus.PUBLISHED,
      modifier: user && user.username,
    })
      .then((res: any) => {
        refreshData();
      })
      .catch((err: any) => {
        const errorCode = err?.code;
        if (errorCode === 'E010' && row.status == PublishStatus.UNPUBLISHED) {
          return dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: 'Templates cannot be published when the associated special day is unpublished',
              },
            ]),
          );
        }

        return dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: 'Failed, please try again.',
            },
          ]),
        );
      });
  };

  const onCancel = () => {
    setPopUpVisible(false);
    setInitialValues(initialTemplateValue);
    setTemplateId('');
    setTimeout(() => {
      setIsView(false);
    }, 200);
  };

  const onReset = () => {
    setFilterData(filterInitData);
  };

  return (
    <>
      <PruForm
        config={filterConfig(Translation, onSearch, onReset)}
        data={filterData}
        onChange={setFilterData}
        space={20}
        style={{ padding: 20 }}
      ></PruForm>
      <PruForm
        config={tableHeader(Translation, openPopUp, refreshData)}
        data={[]}
        onChange={() => {}}
        space={20}
        style={{ padding: '15px 20px', marginTop: 20, borderTopLeftRadius: 5, borderTopRightRadius: 5 }}
      ></PruForm>
      <DeleteDialog
        key={`delete-dialog-${deleteDialogState.open}`}
        open={deleteDialogState.open}
        templateId={deleteDialogState.id}
        onClose={() => setDeleteDialogState(initialDeleteDialogState)}
        onRefresh={refreshData}
      />

      <TableContainer component={Paper} className="event-category-container">
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {tableConfig(Translation, regionLocale, intl.locale).map((item) => (
                <ProTableCell key={item.prop} dataIndex={item.prop} sorter={item.sorter} handleSort={handleSort}>
                  {item.title}
                </ProTableCell>
              ))}
              <StickyTableCell className={`sticky-cell`}>
                {Translation('component.formLabel.operation')}
              </StickyTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <PruTableLoading isLoading={pageLoading} />
            <PruTableEmptyRow isEmpty={tableData.length === 0} />
            {tableData.length > 0 &&
              tableData.map((row) => (
                <TableRow key={row.templateId}>
                  {tableConfig(Translation, regionLocale, intl.locale).map((config) => (
                    <TableCell key={config.prop}>{config.render ? config.render(row) : row[config.prop]}</TableCell>
                  ))}

                  <StickyTableCell className={`sticky-cell`}>
                    <div className={classes.operationContainer}>
                      {row.status == PublishStatus.UNPUBLISHED && (
                        <div
                          style={{ marginRight: 10 }}
                          className={`${classes.operationBtn}`}
                          onClick={() => onEdit(row)}
                        >
                          {Translation('component.formTitle.edit')}
                        </div>
                      )}
                      {
                        <div
                          style={{ marginRight: 10 }}
                          className={`${classes.operationBtn}`}
                          onClick={() => onChangeStatus(row)}
                        >
                          {row.status == PublishStatus.PUBLISHED
                            ? Translation('app.button.unpublish')
                            : Translation('app.button.publish')}
                        </div>
                      }
                      <div
                        style={{ marginRight: 10 }}
                        className={`${classes.operationBtn}`}
                        onClick={() => onCopy(row)}
                      >
                        Copy
                      </div>
                    </div>
                  </StickyTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableFooter component="div" className={`table-footer-css`}>
        <TableRow component="div">
          <TablePagination
            align="right"
            rowsPerPageOptions={[5, 10, 20, 50]}
            colSpan={12}
            component="div"
            count={pageData.total || 0}
            rowsPerPage={pageData.pageSize}
            page={pageData.pageNum - 1 || 0}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            onPageChange={(e, page) => onChangePage(page + 1)}
            onRowsPerPageChange={(e) => onChangePageRows(e.target.value)}
            ActionsComponent={PruTablePaginationActions}
          />
        </TableRow>
      </TableFooter>

      <Dialog open={popUpVisible} onClose={isView ? onCancel : () => {}} fullWidth>
        <DialogTitle>
          {isView
            ? Translation('global.view.text')
            : templateId
            ? Translation('global.edit.text')
            : Translation('app.button.addNew')}{' '}
          {`${Translation('event.greeting_card.template.form')}`}
        </DialogTitle>
        <PopUp
          key={templateId || initialValues.templateId || 'new-template'}
          visible={popUpVisible}
          onCancel={onCancel}
          onSave={onSave}
          specialList={options}
          intialValues={initialValues}
          confirmLoading={formLoading}
          disabled={isView}
          templateId={templateId}
        />
      </Dialog>
    </>
  );
};

export default TemplateList;
